//// TODO::
//// onSheetCreated() -> this._$scope.collapse();

/**
 * @typedef {(args: {
 *    templateId: string,
 *    title: string,
 *    accountIds: string[]
 * }) => ReturnType<AddGoogleSheetComponent['_SheetsService']['createSheet']>} OnCreateSheetFn
 */


/**
 * @typedef {object} AccountListItem
 * @property {string} id
 * @property {boolean} selected
 * @property {string} institutionName
 * @property {string} accountName
 * @property {string} accountNumber
 */

/**
 * @typedef {object} TemplateSelect
 * @property {string} spreadsheetTitle
 * @property {import('types/api').TemplateFromAPI} [template]
 */

/**
 * @typedef {object} AddGoogleSheetController.OnChanges
 * @property {object} [providerAccounts]
 * @property {import('types/api').ProviderAccountFromAPI[]} [providerAccounts.currentValue]
 * @property {import('types/api').ProviderAccountFromAPI[]} [providerAccounts.previousValue]
 */

/**
 * @typedef {object} AddGoogleSheetController.$Scope
 * @property {number} loading
 * @property {import("types/api").V1.UserFromAPI} user
 * @property {number} maxSheets
 * @property {string} message
 * @property {string} errorMessage
 * @property {string} loadingMessage
 *
 * @property {import('types/api').TemplateFromAPI[]} templates
 * @property {TemplateSelect} templateSelect
 * @property {() => boolean} isATemplateSelected
 *
 * @property {() => boolean} hasPendingSheets
 * @property {unknown[]} pendingSheets
 * @property {() => unknown} createNewSheetClicked
 *
 * @property {AccountListItem[]} accounts
 * @property {() => boolean} areAnyAccountsSelected
 */

/**
 * @typedef {ng.IScope
 *  & import("javascripts/collapsible.controller").IScope
 *  & AddGoogleSheetController.$Scope
 * } AddGoogleSheetController.ScopeArg
 */


/**
 * @param {any} data
 * @return {data is {error: { message: string }}}
 */
function isError(data) {
  return !!data?.error?.message;
}


function maxSheetsForUser(user) {
  if (user?.subscription?.plan?.metadata?.maxSheetsAllowed) {
    return Number(user.subscription.plan.metadata.maxSheetsAllowed) ?? null;
  }
  return null;
}
function maxSheetsStr(val) {
  if (val >= 0 && val < 10) {
    return ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'][val];
  }
  return val;
}
function maxSheetsMsg(val) {
  return 'Create a new sheet using one of our Google Sheets templates, up to a total of ' + maxSheetsStr(val) + ' sheets in your account.'
}


export class AddGoogleSheetComponent {

  defaultMaxSheetsPerUser = 10;

  /** @type {AccountListItem[]} */
  accountVMs = [];

  /** @type {import('types/api').V1.UserFromAPI} */
  user = null;
  loading = 0;
  loadingMessage = null;
  errorMessage =  null;
  templates = [];
  templateSelect = {
    spreadsheetTitle: 'Tiller Sheet'
  };

  /// -- BINDINGS -- ///
  isCollapsed       = false;
  providerAccounts  = [];

  /** @type {OnCreateSheetFn} */
  onCreateSheet     = (args) => null;
  onToggleCollapse  = () => null;

  areAnyAccountsSelected() {
    if (this.accountVMs) {
      return this.accountVMs.some(function(account) {
        return account.selected;
      });
    }
    return false;
  }

  isATemplateSelected() {
    return !!(this.templateSelect
      && this.templateSelect.template
      && this.templateSelect.template.id);
  }

  createNewSheetClicked() {
    this.errorMessage = null;

    this.loadingMessage = "Creating Sheet...";
    this.loading++;

    var accounts = [];
    if (this.accountVMs) {
      this.accountVMs.forEach(function(account) {
        if(account.selected) accounts.push(account.id);
      });
    }

    this.onCreateSheet({
      templateId: this.templateSelect.template.id,
      title: this.templateSelect.spreadsheetTitle,
      accountIds: accounts,
    }).then(data => {
      if (isError(data)) {
        this._reportError(data.error.message);
      } else {
        this.loadingMessage = "Loading...";
        this.loading--;
      }
    }).catch(err => {
      this._$errorReporter(err);
      this._reportError("Currently unable to create new sheets, please try again later.");
      this.loading--;
    });
  }

  /**
   * @param {AddGoogleSheetController.OnChanges} changes
   */
  $onChanges(changes) {
    if (!!changes.providerAccounts?.currentValue) {
      /**
       * When `providerAccounts` on parent scope changes, we need to build
       * ViewModels for this controller.
       */
      const accountList = [];

      for (const providerAccount of changes.providerAccounts.currentValue) {
        for (const account of (providerAccount.accounts ?? [])) {
          accountList.push({
            id: account.id,
            selected: this.accountVMs.find(_ => _.id == account.id)?.selected ?? false,
            institutionName: providerAccount.institution.name,
            accountName: account.name,
            accountNumber: account.number,
          });
        }
      }
      this.accountVMs = accountList;
    }
  }

  $onInit() {
    /**
     * When `providerAccounts` on parent scope changes, we need to build
     * ViewModels for this controller.
     */
    this._UserService.user$.subscribe(user => {
      this._updateUserData(user);
    });

    return this._getTemplates().then(_ => {
      this._selectDefault();
    });
  }

  /**
   * @private
   * @param {string} msg
   */
  _reportError(msg) {
    this.errorMessage = msg;
  }

  /**
   * @private
   * @param {AddGoogleSheetComponent['user']} user
   */
  _updateUserData(user) {
    this.user = user;
    this._updateNewSheetName(user);

    this.maxSheets = maxSheetsForUser(user) ?? this.defaultMaxSheetsPerUser;
    this.message = maxSheetsMsg(this.maxSheets);

    this._$scope.$applyAsync();
  }


  _updateNewSheetName(user) {
    this.templateSelect.spreadsheetTitle = 'Tiller Sheet';

    if (user.lifetimeSheetsCreated > 0) {
      this.templateSelect.spreadsheetTitle += ' #' + (user.lifetimeSheetsCreated + 1).toString();
    }
  }

  _selectDefault() {
    this.templateSelect.template = this.templates[0];

    // Only select the default template for the FIRST sheet.
    if(this.user.lifetimeSheetsCreated == 0) {

      this.templates.some(function(template) {

        if (template.id == this.user.defaultTemplate) {
          this.templateSelect.template = template;
          return true;
        }
        return false;
      });
    }
  };

  _getTemplates() {
    this.loadingMessage = "Loading Templates...";
    this.loading++;

    return this._TemplateResource.query().$promise.then((templates) => {
      this._$scope.$applyAsync((scope) => {
        this.templates = [{name: "Pick one"}].concat(templates);
        this.loading--;
      });
    });
  };

  toggleCollapseClick() {
    // ($ctrl.sheets && $ctrl.sheets.length < $ctrl.maxSheets) &&
    this.onToggleCollapse();
  }



  /** @type {ng.IScope & import('../../collapsible.controller').IScope} */
  _$scope;

  /** @type {import("core-services/user.service").UserService} */
  _UserService;

  /** @type {import('../linked-sheets.module').ITemplateResource} */
  _TemplateResource;
  /** @type {import("core-services/sheets.service").SheetsService} */
  _SheetsService;

  /**
   * @param {AddGoogleSheetController.ScopeArg} $scope
   * @param {AddGoogleSheetComponent['_UserService']} UserService
   * @param {AddGoogleSheetComponent['_TemplateResource']} TemplateResource
   * @param {AddGoogleSheetComponent['_SheetsService']} SheetsService
   * @param {import('stackdriver-errors-js').default['report']} $errorReporter
   * @param {number} DEFAULT_MAX_SHEETS_PER_USER
   */
  constructor(
    $scope,
    UserService,
    TemplateResource,
    SheetsService,
    $errorReporter,
    DEFAULT_MAX_SHEETS_PER_USER,
  ) {
    this._$scope = $scope;
    this._UserService = UserService;
    this._TemplateResource = TemplateResource;
    this._SheetsService = SheetsService;
    this._$errorReporter = $errorReporter;
    this.defaultMaxSheetsPerUser = DEFAULT_MAX_SHEETS_PER_USER;
    this.maxSheets = this.defaultMaxSheetsPerUser;

    if ($scope.setControllerName)
      $scope.setControllerName('AddGoogleSheetComponent');

  }
}

// @ts-ignore
import html from './add-google-sheet.template.html';

export default {
  template: html,
  controller: AddGoogleSheetComponent,
  bindings: {
    isCollapsed: "<",
    isDisabled: "<",
    providerAccounts: "<",
    onCreateSheet: "&",
    onToggleCollapse: "&"
  }
};